import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Package, Star, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductInsightsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiFetch('/api/products/insights', {
      });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (score) => {
    if (score >= 70) return 'text-[#2eb4ac]';
    if (score >= 50) return 'text-[#f59e0b]';
    return 'text-destructive';
  };

  const getHealthBg = (score) => {
    if (score >= 70) return 'bg-[#2eb4ac]';
    if (score >= 50) return 'bg-[#f59e0b]';
    return 'bg-destructive';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-48 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Product Insights</h1>
        <p className="text-muted-foreground mt-1">
          AI-powered health scores and issue detection for each product
        </p>
      </div>

      {/* Products grid */}
      {products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, idx) => (
            <Card
              key={product.product_name}
              className="card-hover stagger-item"
              data-testid={`product-card-${idx}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-heading truncate">
                      {product.product_name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <Package className="w-3 h-3" />
                      {product.brand}
                    </CardDescription>
                  </div>
                  <div
                    className={`text-2xl font-bold font-heading ${getHealthColor(
                      product.health_score
                    )}`}
                  >
                    {product.health_score}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Health Score Bar */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Health Score</span>
                    <span className={getHealthColor(product.health_score)}>
                      {product.health_score >= 70
                        ? 'Good'
                        : product.health_score >= 50
                        ? 'Fair'
                        : 'Needs Attention'}
                    </span>
                  </div>
                  <Progress
                    value={product.health_score}
                    className="h-2"
                    indicatorClassName={getHealthBg(product.health_score)}
                  />
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Star className="w-4 h-4 text-[#f59e0b] fill-[#f59e0b]" />
                      <span className="font-semibold">{product.avg_rating}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Avg Rating</p>
                  </div>
                  <div className="text-center">
                    <span className="font-semibold">{product.total_reviews}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">Reviews</p>
                  </div>
                  <div className="text-center">
                    <span
                      className={`font-semibold ${
                        product.negative_sentiment_pct > 30
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {product.negative_sentiment_pct}%
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">Negative</p>
                  </div>
                </div>

                {/* Top Issues */}
                {product.top_issues?.length > 0 && (
                  <div className="pt-2 border-t border-border">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                      <AlertTriangle className="w-3 h-3" />
                      Top Issues
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {product.top_issues.slice(0, 3).map((issue) => (
                        <Badge
                          key={issue.issue}
                          variant="secondary"
                          className="text-xs"
                        >
                          {issue.issue}
                          <span className="ml-1 text-muted-foreground">({issue.count})</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Product Data</h3>
            <p className="text-muted-foreground">
              Upload reviews in Settings to see product insights.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
